/**
 * Plantilla de la home (/) para visitantes que no entran en el bucket Argentina
 * (cf. usesSpanishDefaultForSite). El Worker sigue enviando BaseTemplate por
 * defecto; Index sustituye por este valor cuando corresponde.
 *
 * **Política**: visitantes internacionales SIEMPRE reciben un template basado en
 * video Mux — convierte mucho mejor que cualquier galería. Por eso la lista
 * solo permite templates con `<MuxVideoPlayerWithCTA>` en el render principal.
 * BaseTemplate / BaseTemplateBlack / BaseTemplateInternational no son opciones:
 * la galería clásica queda reservada a visitantes AR.
 *
 * Templates removidos del allow-list (rompían la conversión Mux internacional):
 *   - ArgentinaBioTemplate (galería provincial, no es Mux-first)
 *   - MaintenanceTemplate (sin video, solo para freeze global)
 */

/** Plantilla fullscreen: `<MuxVideoPlayerWithCTA>` nativo (video + 1 CTA membresía Stripe). */
export const INVITACION_EXCLUSIVA_MUX_TEMPLATE = "InvitacionExclusivaMux" as const;

/** Plantilla fullscreen: `<MuxVideoPlayerWithCTA>` + overlay dual (membresía + canal gratis). */
export const DUAL_CTA_VIDEO_LANDING_TEMPLATE = "DualCTAVideoLanding" as const;

/**
 * Default no-AR: **DualCTAVideoLanding** — `<MuxVideoPlayerWithCTA>` fullscreen
 * gestionable en admin (Mux por país) y embudo **Membresía Exclusiva** (Stripe)
 * desde el init; canal gratis opcional/secundario.
 * InvitacionExclusivaMux = variante admin (1 CTA nativo del player).
 */
export const DEFAULT_INTERNATIONAL_HOME_TEMPLATE = DUAL_CTA_VIDEO_LANDING_TEMPLATE;

export const ALLOWED_INTERNATIONAL_HOME_TEMPLATES = [
  "InvitacionExclusivaMux",
  DUAL_CTA_VIDEO_LANDING_TEMPLATE,
  "BioTemplatePremiumSimple",
  "BioTemplatePremium",
  "BioLinkTemplate",
] as const;

export type InternationalHomeTemplateName = (typeof ALLOWED_INTERNATIONAL_HOME_TEMPLATES)[number];

const ALLOWED_SET = new Set<string>(ALLOWED_INTERNATIONAL_HOME_TEMPLATES);

/**
 * Plantillas cuya home en `/` consume `internationalHomeMuxByCountry` y pasan
 * el playback ID directo a `<MuxVideoPlayerWithCTA>` (fullscreen al cargar).
 */
export const INTERNATIONAL_HOME_TEMPLATES_WITH_MUX_OVERRIDE = [
  "InvitacionExclusivaMux",
  DUAL_CTA_VIDEO_LANDING_TEMPLATE,
  "BioTemplatePremiumSimple",
  "BioTemplatePremium",
] as const;

const MUX_OVERRIDE_SET = new Set<string>(INTERNATIONAL_HOME_TEMPLATES_WITH_MUX_OVERRIDE);

/** Todas las plantillas Mux-first admitidas en la home internacional. */
export const INTERNATIONAL_HOME_MUX_PLAYER_TEMPLATES: ReadonlySet<string> = ALLOWED_SET;

/** true si la plantilla admite playback ID Mux por país desde /admin/country-templates. */
export function internationalHomeTemplateSupportsMuxOverride(
  template: string | null | undefined,
): boolean {
  if (typeof template !== "string" || !template.trim()) return false;
  return MUX_OVERRIDE_SET.has(template.trim());
}

/** true si la plantilla internacional renderiza `<MuxVideoPlayerWithCTA>` en la home. */
export function isInternationalHomeMuxPlayerTemplate(
  template: string | null | undefined,
): boolean {
  if (typeof template !== "string" || !template.trim()) return false;
  return ALLOWED_SET.has(template.trim());
}

/** true si la plantilla es InvitacionExclusivaMux (MuxVideoPlayerWithCTA directo en `/`). */
export function isInvitacionExclusivaMuxTemplate(
  template: string | null | undefined,
): boolean {
  if (typeof template !== "string" || !template.trim()) return false;
  return template.trim() === INVITACION_EXCLUSIVA_MUX_TEMPLATE;
}

/** true si la plantilla es DualCTAVideoLanding (Mux fullscreen al cargar `/`). */
export function isDualCtaVideoLandingTemplate(
  template: string | null | undefined,
): boolean {
  if (typeof template !== "string" || !template.trim()) return false;
  return template.trim() === DUAL_CTA_VIDEO_LANDING_TEMPLATE;
}

/**
 * Visitante en embudo DualCTA de conversión (init `/` no-AR).
 * Solo AR explícito queda fuera: UNKNOWN/vacío se tratan como intl para
 * no ocultar el CTA Stripe en el init (VPN, geo lenta, etc.).
 */
export function isInternationalDualCtaVisitor(
  country: string | null | undefined,
): boolean {
  const code =
    typeof country === "string" ? country.trim().toUpperCase().slice(0, 2) : "";
  return code !== "AR";
}

/**
 * CTA de membresía en DualCTAVideoLanding — siempre on en el init no-AR
 * (Stripe USD → portal). No depende de flags BioTemplate.
 */
export function isDualCtaVipMembershipEnabled(
  country: string | null | undefined,
): boolean {
  return true;
}

/** true si `/` arranca directo en `<MuxVideoPlayerWithCTA>` (nativo o DualCTA). */
export function isMuxVideoPlayerHomeTemplate(
  template: string | null | undefined,
): boolean {
  return (
    isInvitacionExclusivaMuxTemplate(template) ||
    isDualCtaVideoLandingTemplate(template)
  );
}

/** Plantillas cuyo checkout Stripe VIP respeta precio/descuento por país (admin). */
export function internationalHomeTemplateSupportsVipPricingOverride(
  template: string | null | undefined,
): boolean {
  return isMuxVideoPlayerHomeTemplate(template);
}

/** Opciones para el admin (orden de presentación). Solo templates Mux-first. */
export const INTERNATIONAL_HOME_TEMPLATE_OPTIONS: {
  value: InternationalHomeTemplateName;
  label: string;
}[] = [
  {
    value: DUAL_CTA_VIDEO_LANDING_TEMPLATE,
    label:
      "DualCTAVideoLanding — Mux + Membresía Exclusiva (Stripe) desde el init; canal gratis opcional — recomendado no-AR",
  },
  {
    value: "InvitacionExclusivaMux",
    label: "Mux fullscreen + CTA membresía (MuxVideoPlayerWithCTA nativo)",
  },
  { value: "BioTemplatePremiumSimple", label: "Premium simple (Mux video + Stripe)" },
  { value: "BioTemplatePremium", label: "Premium invitación (Mux video + plan único)" },
  { value: "BioLinkTemplate", label: "Bio link (video Mux fullscreen)" },
];

/** Valida y devuelve plantilla internacional para la home; inválido → default. */
export function normalizeInternationalHomeTemplate(
  raw: string | null | undefined,
): InternationalHomeTemplateName {
  if (typeof raw !== "string" || !raw.trim()) return DEFAULT_INTERNATIONAL_HOME_TEMPLATE;
  const t = raw.trim();
  if (ALLOWED_SET.has(t)) return t as InternationalHomeTemplateName;
  return DEFAULT_INTERNATIONAL_HOME_TEMPLATE;
}

/** Mapa país→plantilla (override por país de la home internacional). Clave = ISO2 mayúsculas. */
export type InternationalHomeTemplateByCountry = Record<string, InternationalHomeTemplateName>;

/**
 * Normaliza un mapa país→plantilla desde admin/API (objeto o JSON string).
 * Descarta claves no-ISO2 y plantillas no permitidas (no las coacciona al default).
 */
export function parseInternationalHomeTemplateByCountry(
  raw: unknown,
): InternationalHomeTemplateByCountry {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return {};
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
  const out: InternationalHomeTemplateByCountry = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const code = String(k).trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    if (typeof v !== "string" || !ALLOWED_SET.has(v.trim())) continue;
    out[code] = v.trim() as InternationalHomeTemplateName;
  }
  return out;
}

/**
 * Plantilla efectiva para un visitante internacional: override por país si existe,
 * si no el valor global (`internationalHomeTemplate`).
 */
export function resolveInternationalHomeTemplate(
  country: string | null | undefined,
  /** Acepta el mapa laxo (Record<string,string>) que expone useSiteSettings;
   *  el valor por país se normaliza para no filtrar plantillas inválidas. */
  byCountry: Record<string, string> | null | undefined,
  globalValue: string | null | undefined,
): InternationalHomeTemplateName {
  const code = typeof country === "string" ? country.trim().toUpperCase() : "";
  const override = code && byCountry ? byCountry[code] : undefined;
  if (override) return normalizeInternationalHomeTemplate(override);
  return normalizeInternationalHomeTemplate(globalValue);
}

// ─── Mux playback ID por país ────────────────────────────────────────────────
// Soporta 1 o N variantes por país (A/B). Ver internationalHomeMuxVariants.ts.
// Legacy: { "US": "playbackId" }. Multi: { "US": { variants: [...] } }.

export {
  isValidMuxPlaybackId,
  parseInternationalHomeMuxByCountry,
  parseInternationalHomeMuxByCountryRich,
  parseMuxCountryConfig,
  flattenMuxByCountryToPlaybackMap,
  resolveInternationalHomeMuxVariantForCountry,
  getCountryVariantsList,
  emptyVariant,
  type MuxCountryVariant,
  type MuxCountryConfig,
  type InternationalHomeMuxByCountry,
  type InternationalHomeMuxByCountryRaw,
  type ResolvedMuxVariant,
} from "@/lib/internationalHomeMuxVariants";

import {
  isValidMuxPlaybackId,
  parseMuxCountryConfig,
  resolveInternationalHomeMuxVariantForCountry,
  type InternationalHomeMuxByCountry,
  type InternationalHomeMuxByCountryRaw,
} from "@/lib/internationalHomeMuxVariants";

/**
 * Playback ID Mux efectivo para un país (primera variante sticky si hay A/B).
 * Devuelve null si no hay override — el caller usa fallbacks.
 */
export function resolveInternationalHomeMuxForCountry(
  country: string | null | undefined,
  byCountry:
    | InternationalHomeMuxByCountryRaw
    | Record<string, string>
    | null
    | undefined,
): string | null {
  const resolved = resolveInternationalHomeMuxVariantForCountry(
    country,
    byCountry,
  );
  return resolved?.playbackId ?? null;
}

/**
 * Plantilla efectiva que renderiza `Index` en `/`.
 *
 * - **Internacional (no-AR):** manda admin (`internationalHomeTemplate` global +
 *   overrides por país). El Worker puede inyectar `BioTemplatePremiumSimple` u otra
 *   plantilla geo en `__TEMPLATE_CONFIG__`, pero la elección del admin en
 *   `/admin/country-templates` tiene prioridad (p. ej. InvitacionExclusivaMux).
 * - **Argentina:** flujo Worker por defecto; opt-in explícito vía override `AR`.
 */
/**
 * Esperar `/api/site-settings` antes de pintar la home.
 *
 * - **Internacional / AR Mux (Exclusivo|dual_video):** sí — playback VIP y overrides
 *   deben estar listos (evita flash BaseTemplate → DualCTA).
 * - **Argentina galería (sin CTA Mux):** no — Worker ya manda BaseTemplate; pintar
 *   de inmediato evita pantalla negra en 4G / IG WebView.
 *
 * Nota: con cache client caliente `isLoading` ya es false y no se bloquea igual.
 */
export function shouldWaitForSiteSettingsBeforeHomeRender(
  isArgentinaVisitor: boolean,
  /** True when home will render DualCTA / InvitacionExclusivaMux (incl. AR Exclusivo). */
  isMuxHome = false,
): boolean {
  if (isMuxHome) return true;
  return !isArgentinaVisitor;
}

/**
 * AR wishlist CTA “Exclusivo” (community) o dual_video → home `/` = DualCTAVideoLanding
 * (mismo componente que intl; surface invitacion_exclusiva en Index).
 */
export function isArExclusiveMuxHomeFromEdge(options: {
  isArgentinaVisitor: boolean;
  showCommunity?: boolean | null;
  showDualVideo?: boolean | null;
}): boolean {
  if (!options.isArgentinaVisitor) return false;
  return options.showCommunity === true || options.showDualVideo === true;
}

export function resolveEffectiveHomeTemplate(options: {
  isArgentinaVisitor: boolean;
  country?: string | null;
  workerTemplateName?: string | null;
  internationalHomeTemplate?: string | null;
  internationalHomeTemplateByCountry?: Record<string, string> | null;
  /** Worker edge: wishlist CTA community → Exclusivo (public AR convert). */
  showCommunity?: boolean | null;
  /** Worker edge: dual_video CTA. */
  showDualVideo?: boolean | null;
}): InternationalHomeTemplateName | string {
  const intl = resolveInternationalHomeTemplate(
    options.country,
    options.internationalHomeTemplateByCountry,
    options.internationalHomeTemplate,
  );

  const workerTpl = (options.workerTemplateName || "").trim();

  // ── AR public: Exclusivo / dual_video → DualCTAVideoLanding on init `/` ──
  // Admin assigns community CTA in wishlist-config; Worker sets showCommunity.
  // Same UX as intl DualCTA (Mux fullscreen convert), not BaseTemplate button.
  if (
    isArExclusiveMuxHomeFromEdge({
      isArgentinaVisitor: options.isArgentinaVisitor,
      showCommunity: options.showCommunity,
      showDualVideo: options.showDualVideo,
    })
  ) {
    // Explicit AR country-templates override still wins (rare admin path).
    const arRaw = options.internationalHomeTemplateByCountry?.["AR"];
    if (typeof arRaw === "string" && arRaw.trim()) {
      const normalized = normalizeInternationalHomeTemplate(arRaw);
      if (isMuxVideoPlayerHomeTemplate(normalized)) return normalized;
    }
    if (
      workerTpl === DUAL_CTA_VIDEO_LANDING_TEMPLATE ||
      workerTpl === INVITACION_EXCLUSIVA_MUX_TEMPLATE
    ) {
      return workerTpl;
    }
    return DUAL_CTA_VIDEO_LANDING_TEMPLATE;
  }

  // Worker already injected Mux convert home — never demote to gallery on `/`.
  if (
    workerTpl === DUAL_CTA_VIDEO_LANDING_TEMPLATE ||
    workerTpl === INVITACION_EXCLUSIVA_MUX_TEMPLATE
  ) {
    // Admin country override still wins for non-AR (e.g. force InvitacionExclusivaMux).
    if (!options.isArgentinaVisitor) {
      return intl || workerTpl;
    }
  }

  if (options.isArgentinaVisitor) {
    const arRaw = options.internationalHomeTemplateByCountry?.["AR"];
    if (typeof arRaw === "string" && arRaw.trim()) {
      return normalizeInternationalHomeTemplate(arRaw);
    }
    return options.workerTemplateName ?? "BaseTemplate";
  }

  // Every non-AR country: DualCTA (or admin override) on init `/` — never BaseTemplate.
  return intl || DUAL_CTA_VIDEO_LANDING_TEMPLATE;
}

/**
 * Quita playback IDs huérfanos: solo conserva entradas cuya plantilla efectiva
 * admite override Mux (`internationalHomeTemplateSupportsMuxOverride`).
 */
/**
 * Quita entradas huérfanas y normaliza multi-variante.
 * Conserva el shape rich (string | { variants }) para A/B.
 */
export function pruneInternationalHomeMuxByCountry(
  muxByCountry:
    | InternationalHomeMuxByCountryRaw
    | Record<string, string>
    | null
    | undefined,
  globalTpl: string,
  byCountry: Record<string, string>,
): InternationalHomeMuxByCountryRaw {
  const global = normalizeInternationalHomeTemplate(globalTpl);
  const out: InternationalHomeMuxByCountryRaw = {};
  if (!muxByCountry) return out;
  for (const [rawCode, entry] of Object.entries(muxByCountry)) {
    const code = rawCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const effectiveTpl = normalizeInternationalHomeTemplate(
      byCountry[code] ?? global,
    );
    if (!internationalHomeTemplateSupportsMuxOverride(effectiveTpl)) continue;
    const cfg = parseMuxCountryConfig(entry);
    if (!cfg) continue;
    const enabled = cfg.variants.filter(
      (v) => v.enabled && isValidMuxPlaybackId(v.playbackId),
    );
    if (!enabled.length) continue;
    if (enabled.length === 1 && cfg.variants.length === 1) {
      out[code] = enabled[0].playbackId;
    } else {
      out[code] = { variants: cfg.variants };
    }
  }
  return out;
}

// ─── VIP pricing por país (DualCTAVideoLanding drawer) ───────────────────────

/** Stripe Price ID format (price_…). */
export function isValidStripePriceId(raw: unknown): raw is string {
  return typeof raw === "string" && /^price_[A-Za-z0-9]+$/.test(raw.trim());
}

/** Stripe Product ID format (prod_…). */
export function isValidStripeProductId(raw: unknown): raw is string {
  return typeof raw === "string" && /^prod_[A-Za-z0-9]+$/.test(raw.trim());
}

/** Stripe Promotion Code ID format (promo_…). */
export function isValidStripePromotionCodeId(raw: unknown): raw is string {
  return typeof raw === "string" && /^promo_[A-Za-z0-9]+$/.test(raw.trim());
}

/**
 * Precio VIP configurable por país desde /admin/country-templates.
 * Con `stripePriceId` el checkout cobra ese Price de Stripe; sin él la entrada
 * es solo hint de display y el cobro cae al fallback canónico.
 */
export type InternationalHomeVipPricing = {
  /** Precio mensual completo en USD (display; lo que muestra el drawer). */
  fullPriceUsd: number;
  /** Stripe Price ID (`price_…`). Requerido para override de cobro real. */
  stripePriceId?: string;
  /** Stripe Product ID (`prod_…`) — opcional, audit/display. */
  stripeProductId?: string;
  /** Si true y hay `promotionCodeId`, aplica ese promo en Checkout. */
  applyDiscount?: boolean;
  /** Stripe Promotion Code ID (`promo_…`) usado cuando `applyDiscount`. */
  promotionCodeId?: string;
  /** Descuento del primer mes en % entero (0–90) — solo copy UI del drawer. */
  firstMonthDiscountPct?: number;
};

export type InternationalHomeVipPricingByCountry = Record<string, InternationalHomeVipPricing>;

function normalizeVipPricingEntry(raw: unknown): InternationalHomeVipPricing | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const full =
    typeof o.fullPriceUsd === "number"
      ? o.fullPriceUsd
      : typeof o.fullPriceUsd === "string"
        ? parseFloat(o.fullPriceUsd)
        : NaN;
  if (!Number.isFinite(full) || full <= 0 || full > 9999.99) return null;

  const stripePriceIdRaw =
    typeof o.stripePriceId === "string" ? o.stripePriceId.trim() : "";
  const stripePriceId = isValidStripePriceId(stripePriceIdRaw)
    ? stripePriceIdRaw
    : undefined;

  const stripeProductIdRaw =
    typeof o.stripeProductId === "string" ? o.stripeProductId.trim() : "";
  const stripeProductId = isValidStripeProductId(stripeProductIdRaw)
    ? stripeProductIdRaw
    : undefined;

  const applyDiscount = o.applyDiscount === true || o.applyDiscount === "true";

  const promotionCodeIdRaw =
    typeof o.promotionCodeId === "string" ? o.promotionCodeId.trim() : "";
  const promotionCodeId = isValidStripePromotionCodeId(promotionCodeIdRaw)
    ? promotionCodeIdRaw
    : undefined;

  let discount = 0;
  if (o.firstMonthDiscountPct !== undefined && o.firstMonthDiscountPct !== null) {
    const d =
      typeof o.firstMonthDiscountPct === "number"
        ? o.firstMonthDiscountPct
        : typeof o.firstMonthDiscountPct === "string"
          ? parseFloat(o.firstMonthDiscountPct)
          : NaN;
    if (Number.isFinite(d) && d > 0) discount = Math.min(90, Math.max(1, Math.round(d)));
  }

  // applyDiscount without a valid promo → treat as no discount (don't invent FIRSTANA).
  const discountOn = applyDiscount && Boolean(promotionCodeId);

  return {
    fullPriceUsd: Math.round(full * 100) / 100,
    ...(stripePriceId ? { stripePriceId } : {}),
    ...(stripeProductId ? { stripeProductId } : {}),
    ...(discountOn ? { applyDiscount: true, promotionCodeId } : {}),
    ...(discount > 0 ? { firstMonthDiscountPct: discount } : {}),
  };
}

/** Normaliza mapa país→precio VIP desde admin/API. */
export function parseInternationalHomeVipPricingByCountry(
  raw: unknown,
): InternationalHomeVipPricingByCountry {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return {};
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
  const out: InternationalHomeVipPricingByCountry = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const code = String(k).trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const entry = normalizeVipPricingEntry(v);
    if (entry) out[code] = entry;
  }
  return out;
}

/** Override de precio VIP para un país; null si no hay entrada válida. */
export function resolveInternationalHomeVipPricingForCountry(
  country: string | null | undefined,
  byCountry: InternationalHomeVipPricingByCountry | null | undefined,
): InternationalHomeVipPricing | null {
  const code = typeof country === "string" ? country.trim().toUpperCase() : "";
  if (!code || !byCountry) return null;
  return byCountry[code] ?? null;
}

/** Centavos del primer cobro (con descuento de primer mes si aplica). */
export function resolveVipFirstChargeCents(
  fullPriceUsd: number,
  firstMonthDiscountPct?: number,
): { fullPriceCents: number; chargeCents: number; discountPct: number } {
  const fullPriceCents = Math.round(fullPriceUsd * 100);
  const discountPct =
    typeof firstMonthDiscountPct === "number" && firstMonthDiscountPct > 0
      ? Math.min(90, Math.max(1, Math.round(firstMonthDiscountPct)))
      : 0;
  const chargeCents =
    discountPct > 0
      ? Math.round(fullPriceCents * (1 - discountPct / 100))
      : fullPriceCents;
  return { fullPriceCents, chargeCents, discountPct };
}

/**
 * Quita entradas de precio huérfanas: conserva países cuya plantilla efectiva
 * usa checkout VIP Stripe en `<MuxVideoPlayerWithCTA>` (DualCTA + InvitacionExclusivaMux).
 */
export function pruneInternationalHomeVipPricingByCountry(
  pricingByCountry: InternationalHomeVipPricingByCountry,
  globalTpl: string,
  byCountry: Record<string, string>,
): InternationalHomeVipPricingByCountry {
  const global = normalizeInternationalHomeTemplate(globalTpl);
  const out: InternationalHomeVipPricingByCountry = {};
  for (const [rawCode, entry] of Object.entries(pricingByCountry)) {
    const code = rawCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const effectiveTpl = normalizeInternationalHomeTemplate(byCountry[code] ?? global);
    if (!internationalHomeTemplateSupportsVipPricingOverride(effectiveTpl)) continue;
    const normalized = normalizeVipPricingEntry(entry);
    if (normalized) out[code] = normalized;
  }
  return out;
}

// ─── Canal gratis DualCTA por país (/admin/country-templates) ────────────────

export type InternationalHomeDualCtaFreeTelegramByCountry = Record<string, boolean>;

/** Normaliza mapa país→boolean desde admin/API. */
export function parseInternationalHomeDualCtaFreeTelegramByCountry(
  raw: unknown,
): InternationalHomeDualCtaFreeTelegramByCountry {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return {};
    try {
      obj = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return {};
  const out: InternationalHomeDualCtaFreeTelegramByCountry = {};
  for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
    const code = String(k).trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    if (typeof v === "boolean") out[code] = v;
    else if (v === "true" || v === "on") out[code] = true;
    else if (v === "false" || v === "off") out[code] = false;
  }
  return out;
}

/**
 * Resuelve si el botón Canal gratis debe mostrarse.
 * Prioridad: override país → global site_settings → premium-invite-config → null (mostrar si hay URL).
 */
export function resolveInternationalHomeDualCtaFreeTelegramEnabled(
  country: string | null | undefined,
  globalEnabled: boolean | null | undefined,
  byCountry: InternationalHomeDualCtaFreeTelegramByCountry | null | undefined,
  premiumInviteEnabled: boolean | null | undefined,
): boolean | null {
  const code = typeof country === "string" ? country.trim().toUpperCase() : "";
  if (code && byCountry && Object.prototype.hasOwnProperty.call(byCountry, code)) {
    return byCountry[code];
  }
  if (globalEnabled === true || globalEnabled === false) return globalEnabled;
  if (premiumInviteEnabled === true || premiumInviteEnabled === false) {
    return premiumInviteEnabled;
  }
  return null;
}

/** Quita overrides huérfanos: solo países con plantilla efectiva DualCTAVideoLanding. */
export function pruneInternationalHomeDualCtaFreeTelegramByCountry(
  freeTelegramByCountry: InternationalHomeDualCtaFreeTelegramByCountry,
  globalTpl: string,
  byCountry: Record<string, string>,
): InternationalHomeDualCtaFreeTelegramByCountry {
  const global = normalizeInternationalHomeTemplate(globalTpl);
  const out: InternationalHomeDualCtaFreeTelegramByCountry = {};
  for (const [rawCode, enabled] of Object.entries(freeTelegramByCountry)) {
    const code = rawCode.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) continue;
    const effectiveTpl = normalizeInternationalHomeTemplate(byCountry[code] ?? global);
    if (!isDualCtaVideoLandingTemplate(effectiveTpl)) continue;
    out[code] = Boolean(enabled);
  }
  return out;
}